(function () {
  'use strict';

  // NOTE: Frengee already renders its own `.frengee-pickup__store-name`
  // header with the real store name — nothing to inject for that.
  //
  // `.color-scheme-2` is applied only to small, content-free elements
  // (the accent bar, the icon badge) — never to the outer card or
  // anything wrapping real text. The theme's global scheme rule sets
  // literal `color` on any element carrying that class, so putting it
  // on a wrapper repaints all inherited text inside (this broke the
  // spec table's tab labels earlier — same trap, avoided here).

  function getIconMarkup() {
    var template = document.getElementById('frengee-pickup-store-icon');
    return template ? template.innerHTML.trim() : '';
  }

  function getTimerIconMarkup() {
    var template = document.getElementById('frengee-pickup-timer-icon');
    return template ? template.innerHTML.trim() : '';
  }

  function addAccentBar(pickup) {
    if (pickup.querySelector('.frengee-pickup__accent')) return;
    var accent = document.createElement('span');
    accent.className = 'frengee-pickup__accent color-scheme-2';
    accent.setAttribute('aria-hidden', 'true');
    pickup.insertBefore(accent, pickup.firstChild);
    return accent;
  }

  // Reads the accent bar's real rendered color (whatever scheme 2 actually
  // resolves to for this client/theme) and exposes it as a CSS variable on
  // the card, so the CSS can use it for a soft background wash/border tint
  // without hardcoding a color or fighting the theme's own scheme system.
  function setAccentColorVar(pickup, accent) {
    if (!accent || pickup.style.getPropertyValue('--frengee-accent-rgb')) return;
    var bg = getComputedStyle(accent).backgroundColor;
    var match = bg && bg.match(/\d+(\.\d+)?/g);
    if (!match || match.length < 3) return;
    pickup.style.setProperty('--frengee-accent-rgb', match[0] + ', ' + match[1] + ', ' + match[2]);
  }

  // Real DOM (confirmed live): .frengee-pickup__summary is a flex row
  // holding THREE items — our store icon, Frengee's own status icon
  // (.frengee-pickup__icon, a check/cross glyph), and a `.frengee-pickup__text`
  // column that stacks title + subtext + the "Check other stores" button.
  // `align-items:center` on that row centers the two icons against the
  // *whole* 3-line text column, which visually lands them next to the
  // middle line (the subtext) instead of the title — that was the
  // reported "weird alignment".
  //
  // Fix: pull the two icons and the title out into their own header row,
  // and leave subtext + button in `.frengee-pickup__text` below, full
  // width.
  //
  // NOT a one-time operation: confirmed live that Frengee periodically
  // re-renders its own status icon (swapping the check/cross span when
  // live status data updates), re-inserting the fresh element straight
  // into `.frengee-pickup__summary` — bypassing our header. So every
  // pass re-homes whatever's currently a stray direct child of summary
  // (title/status-icon not yet inside the header) back into it, rather
  // than running once and trusting the DOM to stay put.
  function restructureSummary(block) {
    var summary = block.querySelector('.frengee-pickup__summary');
    if (!summary) return;

    var textWrap = summary.querySelector('.frengee-pickup__text');
    // Look for the title anywhere in summary, not just inside textWrap —
    // after the first successful run it lives in the header instead, and
    // scoping this to textWrap made the guard below bail out on every
    // later call, which is exactly what silently blocked the status icon
    // from ever being re-homed once Frengee re-inserted it later.
    var title = summary.querySelector('.frengee-pickup__title');
    if (!textWrap || !title) return;

    var header = summary.querySelector('.frengee-pickup__header');
    if (!header) {
      header = document.createElement('div');
      header.className = 'frengee-pickup__header';
      summary.insertBefore(header, summary.firstChild);
    }

    // Move title into the header FIRST, so it's always a valid reference
    // node for the insertBefore calls below — re-homing storeIcon/
    // statusIcon before title existed in the header threw (insertBefore
    // requires the reference node to already be a child of that parent).
    if (title.parentElement !== header) header.appendChild(title);

    // Re-home anything that's drifted back to being a direct child of
    // summary instead of living in the header, always placed BEFORE the
    // title. A plain appendChild here would drop the re-homed status
    // icon at the end of header's children — since title is usually
    // placed first, that put the dot AFTER the title text (confirmed
    // live, matches the reported "dot floats after the text" bug)
    // instead of between the store icon and the title.
    var storeIcon = summary.querySelector(':scope > .frengee-pickup__store-icon');
    if (storeIcon) header.insertBefore(storeIcon, title);

    // Confirmed live: Frengee uses TWO DIFFERENT class names for this
    // same semantic element depending on state — `.frengee-pickup__icon`
    // for the available case, `.frengee-store__cross` for the
    // unavailable case (both still carry the same
    // `frengee-pickup__status--available`/`--unavailable` modifier
    // class, just on a different base class). Re-home whichever one
    // Frengee actually rendered.
    var statusIcon = summary.querySelector(':scope > .frengee-pickup__icon, :scope > .frengee-store__cross');
    if (statusIcon) header.insertBefore(statusIcon, title);
  }

  // Belt-and-suspenders only: in earlier testing a product with zero
  // pickup locations rendered no status element in the summary at all.
  // Later testing on a different unavailable product showed Frengee DID
  // render a real one there (`.frengee-store__cross`, just a different
  // base class than the available case's `.frengee-pickup__icon`) — so
  // this now only fires as a last resort if genuinely NEITHER real
  // element shows up, rather than assuming the unavailable case never
  // has one. Checks fresh every pass (no one-time guard) so it can't get
  // stuck with a stale state if it fires once before the title settles.
  function ensureStatusDot(block) {
    var summary = block.querySelector('.frengee-pickup__summary');
    var header = summary && summary.querySelector('.frengee-pickup__header');
    var title = header && header.querySelector('.frengee-pickup__title');
    if (!header || !title) return;

    var existing = header.querySelector('.frengee-pickup__icon, .frengee-store__cross');
    var titleText = title.textContent.trim();
    var unavailableLabel = (block.dataset.unavailableLabel || '').trim();
    var pickupPrefix = (block.dataset.pickupPrefix || '').trim();

    var isUnavailable = unavailableLabel && titleText.indexOf(unavailableLabel) === 0;
    var isAvailable = !isUnavailable && pickupPrefix && titleText.indexOf(pickupPrefix) === 0;
    // Fallback if the data attributes aren't present for some reason —
    // keeps this working even if Frengee changes those attribute names.
    if (!isUnavailable && !isAvailable) {
      isUnavailable = /unavailable/i.test(titleText);
      isAvailable = !isUnavailable;
    }

    // A real element exists — make sure ITS modifier class actually
    // matches the current title (Frengee's own class can otherwise lag
    // behind a title update by one render pass), then stop.
    if (existing) {
      existing.classList.toggle('frengee-pickup__status--available', isAvailable);
      existing.classList.toggle('frengee-pickup__status--unavailable', isUnavailable);
      return;
    }

    var dot = document.createElement('span');
    dot.className = 'frengee-pickup__icon' + (isAvailable ? ' frengee-pickup__status--available' : ' frengee-pickup__status--unavailable');
    dot.setAttribute('aria-hidden', 'true');
    header.insertBefore(dot, title);
  }

  function addStoreIcon(block) {
    var pickup = block.querySelector('.frengee-pickup');
    var summary = block.querySelector('.frengee-pickup__summary');
    if (!summary) return;

    var accent;
    if (pickup) accent = addAccentBar(pickup) || pickup.querySelector('.frengee-pickup__accent');

    // Insert into the header row once it exists (post-restructure); fall
    // back to the summary itself on the very first pass, before
    // restructureSummary has had a chance to run.
    var iconHost = summary.querySelector('.frengee-pickup__header') || summary;

    if (!iconHost.querySelector('.frengee-pickup__store-icon')) {
      var iconMarkup = getIconMarkup();
      if (iconMarkup) {
        var icon = document.createElement('span');
        icon.className = 'frengee-pickup__store-icon';
        icon.setAttribute('aria-hidden', 'true');
        icon.innerHTML = iconMarkup;
        iconHost.insertBefore(icon, iconHost.firstChild);
      }
    }

    restructureSummary(block);
    ensureStatusDot(block);
    if (pickup) setAccentColorVar(pickup, accent);
  }

  // Reveal the "Check other stores" button only when there's genuinely
  // more than one location — for a single location it just repeats the
  // summary line. Reading the block's own transfer-matrix JSON (each
  // store's real availability data) instead of matching the button's
  // text, since that text is Frengee's own copy and could change/be
  // translated independent of this stylesheet.
  function getStoreCount(block) {
    var script = block.querySelector('[data-frengee-transfer-matrix]');
    if (!script) return 0;
    try {
      var data = JSON.parse(script.textContent);
      return Array.isArray(data.stores) ? data.stores.length : 0;
    } catch (e) {
      return 0;
    }
  }

  function updateMultiStoreLink(block) {
    var link = block.querySelector('.frengee-pickup__link');
    if (!link) return;
    var hasMultipleStores = getStoreCount(block) > 1;
    link.classList.toggle('frengee-pickup__link--multi', hasMultipleStores);
    styleMultiStoreLink(link, hasMultipleStores);
  }

  // Belt-and-suspenders fix: confirmed live (via an exhaustive dump of
  // every stylesheet rule matching this exact button, including a
  // manually injected maximally-specific test rule) that
  // pickup-availability-cards.css's own
  // `.frengee-pickup__link.frengee-pickup__link--multi` rule was NOT
  // winning for `display`/`border-bottom` on this button even though it
  // has the highest specificity and !important on the page — a genuine
  // cascade oddity in this environment that a plain inline style
  // (proven live to always win) sidesteps entirely. Kept intentionally
  // redundant with the CSS rule rather than replacing it, in case the
  // CSS *does* apply correctly in other environments/themes.
  function styleMultiStoreLink(link, isMulti) {
    if (!isMulti) {
      link.style.display = 'none';
      return;
    }
    link.style.display = 'inline-flex';
    link.style.alignItems = 'center';
    link.style.gap = '4px';
    link.style.marginTop = '8px';
    link.style.padding = '0';
    link.style.border = 'none';
    // Underline requested directly (previously a border-bottom trick,
    // which is what wasn't rendering) — plain text-decoration is
    // simpler and immune to the border-shorthand cascade issue above.
    link.style.textDecoration = 'underline';
    link.style.textUnderlineOffset = '2px';
    link.style.fontSize = '12px';
    link.style.fontWeight = '600';
    link.style.color = 'rgb(var(--color-foreground))';
    link.style.background = 'none';
    link.style.cursor = 'pointer';
  }

  // The drawer this button opens is appended directly to <body> by
  // Frengee's own script — NOT nested inside the pickup block — so it
  // has to be found via the button's aria-controls, not a DOM search
  // scoped to the block.
  function addDrawerAccent(block) {
    var link = block.querySelector('.frengee-pickup__link');
    var drawerId = link ? link.getAttribute('aria-controls') : block.dataset.drawerId;
    var drawer = drawerId ? document.getElementById(drawerId) : null;
    var header = drawer ? drawer.querySelector('.frengee-drawer__header') : null;
    if (!header || header.querySelector('.frengee-drawer__accent')) return;

    var accent = document.createElement('span');
    accent.className = 'frengee-drawer__accent color-scheme-2';
    accent.setAttribute('aria-hidden', 'true');
    header.insertBefore(accent, header.firstChild);
  }

  // Turns the existing ready-time line (e.g. "Usually ready in 24
  // hours") into a small pill with a clock icon — real data Frengee
  // already renders, just plain unstyled text by default. Skipped while
  // it still holds the loading placeholder, and skipped entirely if the
  // subtext is empty (nothing to show, e.g. unavailable with no ETA —
  // confirmed live that Frengee leaves this stuck on the loading
  // placeholder text with `hidden` for products unavailable at every
  // location, since there's no real ETA to report).
  function addSubtextIcon(block) {
    var subtext = block.querySelector('[data-frengee-summary-subtext]');
    if (!subtext) return;

    var text = subtext.textContent.trim();
    var loadingLabel = (block.dataset.loadingLabel || '').trim();
    if (!text || text === loadingLabel) return;

    if (subtext.querySelector('.frengee-pickup__subtext-icon')) return;

    var iconMarkup = getTimerIconMarkup();
    if (!iconMarkup) return;

    var textSpan = document.createElement('span');
    textSpan.className = 'frengee-pickup__subtext-text';
    textSpan.textContent = text;

    var icon = document.createElement('span');
    icon.className = 'frengee-pickup__subtext-icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.innerHTML = iconMarkup;

    subtext.textContent = '';
    subtext.appendChild(icon);
    subtext.appendChild(textSpan);
  }

  function processAll() {
    document.querySelectorAll('[data-frengee-pickup-block]').forEach(function (block) {
      addStoreIcon(block);
      updateMultiStoreLink(block);
      addDrawerAccent(block);
      addSubtextIcon(block);
    });
  }

  // NOTE: deliberately setTimeout, not requestAnimationFrame. Confirmed
  // live (via a manual rAF-vs-direct-call test) that rAF callbacks are
  // throttled/paused entirely while the tab is in the background
  // (`document.hidden === true`) — meaning this whole script would
  // silently never run on a backgrounded preview tab (e.g. switching to
  // the theme customizer and back) until it regained focus. setTimeout
  // isn't fully immune to background throttling either, but browsers
  // don't suspend it outright the way they do rAF, so this keeps the
  // card/dot/link fixes applying regardless of tab focus.
  var scheduled = false;
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    setTimeout(function () {
      scheduled = false;
      processAll();
    }, 0);
  }

  schedule();

  new MutationObserver(schedule).observe(document.body, {
    childList: true,
    subtree: true,
    characterData: true,
  });
})();
